/**
 * Linear Flow Service
 * Handles API calls for simplified linear flows
 */

import apiClient from '../../../shared/api/apiClient';
import {
  LinearFlow,
  FlowExecution,
  LinearFlowTemplate,
  CreateCustomTemplatePayload,
} from '../types/linear-flow.types';

export class LinearFlowService {
  private static readonly BASE_URL = '/flows';
  private static readonly TEMPLATES_URL = '/flow-templates';

  /**
   * List custom flow templates visible to the current user (their own + shared).
   */
  static async getCustomTemplates(): Promise<LinearFlowTemplate[]> {
    try {
      const response = await apiClient.get<LinearFlowTemplate[]>(this.TEMPLATES_URL);
      return response.data;
    } catch (error) {
      console.error('Error fetching custom flow templates:', error);
      throw error;
    }
  }

  /**
   * Save the current flow's steps as a reusable custom template.
   */
  static async createCustomTemplate(
    payload: CreateCustomTemplatePayload
  ): Promise<LinearFlowTemplate> {
    try {
      const response = await apiClient.post<LinearFlowTemplate>(this.TEMPLATES_URL, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating custom flow template:', error);
      throw error;
    }
  }

  /**
   * Update a custom template owned by the current user (FLOW-13).
   */
  static async updateCustomTemplate(
    id: string,
    payload: Partial<CreateCustomTemplatePayload>
  ): Promise<LinearFlowTemplate> {
    try {
      const response = await apiClient.put<LinearFlowTemplate>(`${this.TEMPLATES_URL}/${id}`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating custom flow template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a custom template owned by the current user (FLOW-13).
   */
  static async deleteCustomTemplate(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.TEMPLATES_URL}/${id}`);
    } catch (error) {
      console.error(`Error deleting custom flow template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get all flows for the current user
   */
  static async getFlows(): Promise<LinearFlow[]> {
    try {
      const response = await apiClient.get<LinearFlow[]>(this.BASE_URL);
      return response.data;
    } catch (error) {
      console.error('Error fetching flows:', error);
      throw error;
    }
  }

  /**
   * Get a single flow by ID
   */
  static async getFlow(id: string): Promise<LinearFlow> {
    try {
      const response = await apiClient.get<LinearFlow>(`${this.BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching flow ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new flow
   */
  static async createFlow(flow: Omit<LinearFlow, 'id' | 'created_at' | 'updated_at'>): Promise<LinearFlow> {
    try {
      const response = await apiClient.post<LinearFlow>(this.BASE_URL, flow);
      return response.data;
    } catch (error) {
      console.error('Error creating flow:', error);
      throw error;
    }
  }

  /**
   * Update an existing flow
   */
  static async updateFlow(id: string, flow: Partial<LinearFlow>): Promise<LinearFlow> {
    try {
      const response = await apiClient.put<LinearFlow>(`${this.BASE_URL}/${id}`, flow);
      return response.data;
    } catch (error) {
      console.error(`Error updating flow ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a flow
   */
  static async deleteFlow(id: string): Promise<void> {
    try {
      await apiClient.delete(`${this.BASE_URL}/${id}`);
    } catch (error) {
      console.error(`Error deleting flow ${id}:`, error);
      throw error;
    }
  }

  /**
   * Toggle flow active status
   */
  static async toggleFlowStatus(id: string, isActive: boolean): Promise<LinearFlow> {
    try {
      const response = await apiClient.patch<LinearFlow>(`${this.BASE_URL}/${id}/status`, {
        is_active: isActive,
      });
      return response.data;
    } catch (error) {
      console.error(`Error toggling flow ${id} status:`, error);
      throw error;
    }
  }

  // FLOW-11: testFlow (POST /flows/:id/test) and getFlowStats
  // (GET /flows/:id/stats) were removed — those endpoints don't exist. Use
  // flowService.triggerFlow (/flows/:id/trigger) and flowService.getAnalytics
  // (/flows/:id/analytics) instead.

  /**
   * Get flow execution history
   */
  static async getFlowExecutions(flowId: string): Promise<FlowExecution[]> {
    try {
      const response = await apiClient.get<FlowExecution[]>(`${this.BASE_URL}/${flowId}/executions`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching executions for flow ${flowId}:`, error);
      throw error;
    }
  }

  /**
   * Validate a flow before saving
   */
  static validateFlow(flow: LinearFlow): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Must have a name
    if (!flow.name || flow.name.trim() === '') {
      errors.push('Flow must have a name');
    }

    // Must have at least one step
    if (!flow.steps || flow.steps.length === 0) {
      errors.push('Flow must have at least one step');
    }

    // First step must be a trigger
    if (flow.steps.length > 0 && flow.steps[0].type !== 'trigger') {
      errors.push('First step must be a trigger');
    }

    // Check each step for required fields
    flow.steps.forEach((step, index) => {
      switch (step.type) {
        case 'trigger':
          if (!step.triggerType) {
            errors.push(`Step ${index + 1}: Trigger type is required`);
          }
          if (step.triggerType === 'property_changed') {
            if (!step.config.propertyName) {
              errors.push(`Step ${index + 1}: Property name is required`);
            }
            if (!step.config.condition) {
              errors.push(`Step ${index + 1}: Condition is required`);
            }
          }
          break;

        case 'send_sms':
          if (!step.message || step.message.trim() === '') {
            errors.push(`Step ${index + 1}: Message is required`);
          }
          if (step.message && step.message.length > 1600) {
            errors.push(`Step ${index + 1}: Message is too long (max 1600 characters)`);
          }
          break;

        case 'delay':
          if (!step.amount || step.amount < 1) {
            errors.push(`Step ${index + 1}: Delay amount must be at least 1`);
          }
          if (!step.unit) {
            errors.push(`Step ${index + 1}: Delay unit is required`);
          }
          break;

        case 'conditional':
          if (!step.conditionField) {
            errors.push(`Step ${index + 1}: Condition field is required`);
          }
          if (!step.operator) {
            errors.push(`Step ${index + 1}: Condition operator is required`);
          }
          break;
      }
    });

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

export default LinearFlowService;

